import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  Bookmark,
  Share2,
  Box,
  Phone,
  MapPin,
  User,
  ChevronLeft,
  Check,
  X as XIcon,
  Ruler,
  CalendarDays,
  BedDouble,
} from 'lucide-react';
import LoopVideo, { filmSrc } from './loopVideo.jsx';
import { isSaved, toggleSaved, subscribeSaved, isAccountSynced } from './savedStore.jsx';
import { prepareShare } from './shareDesign.jsx';
import ShareSheet from './shareSheet.jsx';
import { getDesign, markViewed } from './designsApi.jsx';
import { openProfile, phoneText, telHref, pushBackLayer } from './navBus.jsx';
import {
  fa,
  DEAL_LABELS,
  PROPERTY_LABELS,
  ORIENTATION_LABELS,
  AMENITY_LABELS,
  timeAgo,
  listingTitle,
  buildText,
} from './listingFormat.jsx';
import { createStreetMap } from './streetMap.jsx';
import './listingPage.scss';

// صفحه‌ی کاملِ آگهی (مثلِ صفحه‌ی آگهیِ دیوار، با تمِ خودمان):
//   بالای صفحه: نمای سه‌بعدیِ خانه (فیلم) و بعد عکس‌های ملک — با کشیدنِ افقی
//   عنوان، زمان و استان؛ سه عددِ اصلی (متراژ، ساخت، اتاق)؛ قیمت؛ جدولِ مشخصات؛ امکانات؛ توضیحات؛ نقشه؛ مشاور
//   نوارِ پایین: «اطلاعات تماس» و «ورود به خانه» (سه‌بعدی)

const DesignWalk = lazy(() => import('./designWalk.jsx'));

function Media({ design, onEnter }) {
  const l = design.listing || {};
  const photos = (l.photoUrls || []).filter(Boolean);
  // آگهیِ بدونِ سه‌بعدی: اول عکس‌های خودِ خانه، تصویرِ «بدونِ پلانِ سه‌بعدی» آخر
  const cover = design.filmUrl || design.thumbUrl ? [{ kind: '3d' }] : [];
  const photoSlides = photos.map((url) => ({ kind: 'photo', url }));
  const slides = design.modelUrl || !photoSlides.length ? [...cover, ...photoSlides] : [...photoSlides, ...cover];
  const boxRef = useRef(null);
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const onScroll = () => setIndex(Math.round(Math.abs(el.scrollLeft) / Math.max(1, el.clientWidth)));
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  if (!slides.length) {
    return (
      <div className="listing-page__media is-empty">
        <Box size={34} strokeWidth={1.3} />
      </div>
    );
  }
  return (
    <div className="listing-page__media">
      <div className="listing-page__slides" ref={boxRef}>
        {slides.map((s, i) =>
          s.kind === '3d' ? (
            <div key="3d" className="listing-page__slide is-3d">
              {design.filmUrl ? (
                <LoopVideo src={filmSrc(design)} poster={design.thumbUrl} active={index === 0} preload="auto" />
              ) : (
                <img src={design.thumbUrl} alt="" />
              )}
              {design.modelUrl && (
                <span className="listing-page__badge">
                  <Box size={13} strokeWidth={2} /> نمای سه‌بعدی
                </span>
              )}
              {design.modelUrl && (
                <button type="button" className="listing-page__enter-overlay" onClick={onEnter}>
                  <Box size={18} strokeWidth={2} />
                  <span>ورود به خانه</span>
                </button>
              )}
            </div>
          ) : (
            <div key={s.url} className="listing-page__slide">
              <img src={s.url} alt="" loading={i > 1 ? 'lazy' : 'eager'} />
            </div>
          )
        )}
      </div>
      {slides.length > 1 && (
        <div className="listing-page__dots">
          {slides.map((s, i) => (
            <i key={i} className={i === index ? 'is-on' : ''} />
          ))}
        </div>
      )}
      {slides.length > 1 && (
        <span className="listing-page__counter">
          {fa(index + 1)} / {fa(slides.length)}
        </span>
      )}
    </div>
  );
}

// نقشه‌ی کوچکِ موقعیت — همان نقشه‌ی خیابان‌های سرورِ خودمان؛ فقط وقتی ملک لوکیشن دارد
function MiniMap({ lat, lng }) {
  const ref = useRef(null);
  useEffect(() => {
    const map = createStreetMap(ref.current, { center: [lng, lat], zoom: 15, nav: false });
    map.dragPan.disable();
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();
    map.doubleClickZoom.disable();
    map.keyboard.disable();
    map.boxZoom.disable();
    map.on('load', () => {
      map.addSource('site', {
        type: 'geojson',
        data: { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [lng, lat] } },
      });
      map.addLayer({ id: 'site-glow', type: 'circle', source: 'site', paint: { 'circle-radius': 26, 'circle-color': '#0a66c2', 'circle-opacity': 0.15 } });
      map.addLayer({
        id: 'site-dot',
        type: 'circle',
        source: 'site',
        paint: { 'circle-radius': 6, 'circle-color': '#0a66c2', 'circle-stroke-width': 2, 'circle-stroke-color': '#ffffff' },
      });
    });
    return () => map.remove();
  }, [lat, lng]);
  return <div className="listing-page__map" ref={ref} />;
}

function ListingPage({ design: initial, onClose }) {
  const [design, setDesign] = useState(initial);
  const l = design.listing || {};
  const owner = design.owner || null;
  const [saved, setSaved] = useState(() => isSaved(design.id));
  const [toast, setToast] = useState(null);
  const [walk, setWalk] = useState(null);
  const [showPhone, setShowPhone] = useState(false);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => pushBackLayer(() => closeRef.current && closeRef.current()), []);
  useEffect(() => subscribeSaved(() => setSaved(isSaved(design.id))), [design.id]);
  useEffect(() => {
    prepareShare(design);
    markViewed(design.id);
    // آگهی‌های ذخیره‌شده‌ی قدیمی همه‌ی اطلاعات را ندارند — نسخه‌ی کامل از سرور
    if (!design.modelUrl || !design.listing) {
      getDesign(design.id)
        .then((r) => r && r.design && setDesign((d) => ({ ...r.design, shareCode: d.shareCode })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.id]);

  // صفحه‌ی زیرین اسکرول نخورد
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const showToast = (text) => {
    setToast({ text, key: Date.now() });
  };
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  const handleSave = () => {
    const now = toggleSaved(design);
    setSaved(now);
    showToast(now ? (isAccountSynced() ? 'ذخیره شد' : 'ذخیره شد — روی همین گوشی') : 'از ذخیره‌ها برداشته شد');
  };
  // منوی اشتراکِ خودمان — پیامک گزینه‌ی اول
  const [shareOpen, setShareOpen] = useState(false);
  const handleShare = () => setShareOpen(true);
  const handleShareResult = (r) => {
    if (r === 'copied') showToast('لینک کپی شد');
    else if (r === 'failed') showToast('اشتراک‌گذاری ممکن نشد');
  };
  const handleEnter = () => {
    if (design.modelUrl && design.lightmapUrl && design.planUrl) setWalk(design);
    else showToast('این آگهی نمای سه‌بعدیِ قابلِ ورود ندارد');
  };

  const pricePerMeter = l.dealType === 'sale' && l.price && l.area ? Math.round(l.price / l.area) : null;
  const rows = [
    ['نوع آگهی', DEAL_LABELS[l.dealType]],
    ['نوع ملک', PROPERTY_LABELS[l.propertyType]],
    ['طبقه', l.floor != null ? `${l.floor === 0 ? 'همکف' : fa(l.floor)}${l.totalFloors ? ` از ${fa(l.totalFloors)}` : ''}` : null],
    ['جهت', ORIENTATION_LABELS[l.orientation]],
    ['سن بنا', l.buildYear != null ? (l.buildYear === 0 ? 'نوساز' : `${fa(l.buildYear)} سال`) : null],
    ['آدرس', l.address],
  ].filter(([, v]) => v);
  const amenities = Object.keys(AMENITY_LABELS);
  const have = new Set(l.amenities || []);

  return (
    <div className="listing-page">
      {shareOpen && <ShareSheet design={design} onClose={() => setShareOpen(false)} onResult={handleShareResult} />}
      <header className="listing-page__bar">
        <button type="button" onClick={onClose} aria-label="بازگشت">
          <ArrowRight size={22} strokeWidth={1.9} />
        </button>
        <div className="listing-page__bar-actions">
          <button type="button" onClick={handleShare} aria-label="اشتراک‌گذاری">
            <Share2 size={20} strokeWidth={1.9} />
          </button>
          <button type="button" onClick={handleSave} className={saved ? 'is-on' : ''} aria-label="ذخیره">
            <Bookmark size={21} strokeWidth={1.9} fill={saved ? 'currentColor' : 'none'} />
          </button>
        </div>
      </header>

      <div className="listing-page__scroll">
        <Media design={design} onEnter={handleEnter} />

        <section className="listing-page__head">
          <h1>{listingTitle(design)}</h1>
          <p className="listing-page__sub">
            {timeAgo(design.createdAt)}
            {l.province ? ` در ${l.province}` : ''}
            {design.views ? ` · ${fa(design.views)} بازدید` : ''}
          </p>
        </section>

        <section className="listing-page__stats">
          <div>
            <Ruler size={18} strokeWidth={1.7} />
            <span>متراژ</span>
            <strong>{l.area ? fa(l.area) : '—'}</strong>
          </div>
          <div>
            <CalendarDays size={18} strokeWidth={1.7} />
            <span>ساخت</span>
            <strong>{buildText(l) || '—'}</strong>
          </div>
          <div>
            <BedDouble size={18} strokeWidth={1.7} />
            <span>اتاق</span>
            <strong>{l.rooms != null ? (l.rooms === 0 ? 'ندارد' : fa(l.rooms)) : '—'}</strong>
          </div>
        </section>

        <section className="listing-page__rows">
          {l.dealType === 'rent' ? (
            <>
              <div className="listing-page__row">
                <span>ودیعه</span>
                <strong>{l.deposit ? `${fa(l.deposit)} تومان` : 'توافقی'}</strong>
              </div>
              <div className="listing-page__row">
                <span>اجاره‌ی ماهانه</span>
                <strong>{l.rent ? `${fa(l.rent)} تومان` : l.deposit ? 'رهن کامل' : 'توافقی'}</strong>
              </div>
            </>
          ) : (
            <>
              <div className="listing-page__row">
                <span>قیمت کل</span>
                <strong>{l.price ? `${fa(l.price)} تومان` : 'توافقی'}</strong>
              </div>
              {pricePerMeter && (
                <div className="listing-page__row">
                  <span>قیمت هر متر</span>
                  <strong>{`${fa(pricePerMeter)} تومان`}</strong>
                </div>
              )}
            </>
          )}
          {rows.map(([k, v]) => (
            <div key={k} className="listing-page__row">
              <span>{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
        </section>

        {l.propertyType !== 'land' && (
          <section className="listing-page__section">
            <h2>امکانات</h2>
            <div className="listing-page__amenities">
              {amenities.map((a) => (
                <span key={a} className={have.has(a) ? 'is-on' : ''}>
                  {have.has(a) ? <Check size={15} strokeWidth={2.4} /> : <XIcon size={14} strokeWidth={2} />}
                  {AMENITY_LABELS[a]}
                </span>
              ))}
            </div>
          </section>
        )}

        {l.description && (
          <section className="listing-page__section">
            <h2>توضیحات</h2>
            <p className="listing-page__desc">{l.description}</p>
          </section>
        )}

        {l.site && Number.isFinite(l.site.lat) && Number.isFinite(l.site.lng) && (
          <section className="listing-page__section">
            <h2>
              <MapPin size={16} strokeWidth={2} /> موقعیت تقریبی
            </h2>
            <MiniMap lat={l.site.lat} lng={l.site.lng} />
          </section>
        )}

        {owner && (
          <section className="listing-page__section">
            <button type="button" className="listing-page__owner" onClick={() => openProfile(owner)}>
              <span className="listing-page__avatar">{owner.firstName ? owner.firstName.charAt(0) : <User size={16} />}</span>
              <span className="listing-page__owner-text">
                <strong>
                  {owner.firstName} {owner.lastName}
                </strong>
                <small>{owner.role === 'consultant' ? 'مشاور املاک · دیدنِ همه‌ی آگهی‌ها' : 'آگهی‌دهنده'}</small>
              </span>
              <ChevronLeft size={18} strokeWidth={2} />
            </button>
          </section>
        )}
        <div style={{ height: 96 }} />
      </div>

      <footer className="listing-page__footer">
        {owner && owner.phone ? (
          showPhone ? (
            <a className="listing-page__call is-number" href={telHref(owner.phone)}>
              <Phone size={18} strokeWidth={2} />
              <span dir="ltr">{phoneText(owner.phone)}</span>
            </a>
          ) : (
            <button type="button" className="listing-page__call" onClick={() => setShowPhone(true)}>
              <Phone size={18} strokeWidth={2} />
              <span>اطلاعات تماس</span>
            </button>
          )
        ) : null}
        <button type="button" className="listing-page__enter" onClick={handleEnter} disabled={!design.modelUrl}>
          <Box size={18} strokeWidth={2} />
          <span>{design.modelUrl ? 'ورود به خانه' : 'بدونِ پلانِ سه‌بعدی'}</span>
        </button>
      </footer>

      {toast && (
        <div key={toast.key} className="listing-page__toast">
          {toast.text}
        </div>
      )}
      {walk && (
        <Suspense fallback={null}>
          <DesignWalk design={walk} onClose={() => setWalk(null)} />
        </Suspense>
      )}
    </div>
  );
}

export default ListingPage;
